import { countMetric, distributionMetric, runSafely } from '@/lib/metrics.ts';

import {
    type CategoricalAttributes,
    type MetricFailureReason,
    type MetricName,
    type MetricOutcome,
} from '@/models/metrics.ts';

/**
 * Timing for one operator interaction: the click, whatever requests it fans out into, and the paint
 * that follows. Nothing here knows what is being measured - a domain module binds it to its own metric
 * names and attribute vocabulary.
 *
 * The registry of open interactions is what keeps background work out of the numbers: a settle for a
 * key nobody opened does nothing at all.
 *
 * Suits an interaction that lives and dies inside one page view, which is why the registry is a plain
 * Map and the clock is `performance.now()`. A span that has to survive a reload needs a persisted
 * store and a wall clock instead, the way check-in does it.
 */

interface InteractionTrackerConfig {
    outcomeMetric: MetricName;
    durationMetric: MetricName;
    timeoutMs: number;
    /** Recorded when nothing settles the interaction within `timeoutMs`. */
    timeoutReason: MetricFailureReason;
    /** Names the binding in dev warnings. */
    label: string;
}

interface OpenInteraction<Context> {
    context: Context;
    startedAt: number;
    timeoutId: ReturnType<typeof setTimeout>;
}

interface StartInteractionParams<Context> {
    key: string;
    context: Context;
}

interface SettleInteractionParams<Context> {
    key: string;
    outcome: MetricOutcome;
    /** Failure tag, attached only when `outcome === 'failure'`. */
    reason?: MetricFailureReason;
    /** Attributes only known once the interaction ends, merged over the ones stamped at start. */
    context?: Partial<Context>;
}

interface InteractionTracker<Context> {
    start: (params: StartInteractionParams<Context>) => void;
    settle: (params: SettleInteractionParams<Context>) => void;
    abort: (key: string) => void;
    isOpen: (key: string) => boolean;
}

/**
 * Build a tracker over one pair of metrics. Each tracker owns its own registry, so two domains cannot
 * collide on a key.
 */
const createInteractionTracker = <Context extends CategoricalAttributes>(
    config: InteractionTrackerConfig
): InteractionTracker<Context> => {
    const open = new Map<string, OpenInteraction<Context>>();

    const afterOnePaint = (callback: () => void): void => {
        runSafely(() => {
            globalThis.requestAnimationFrame(() => runSafely(callback, config.label));
        }, config.label);
    };

    // Two frames, so emission lands after the frame holding the new content has been presented rather
    // than when the promise resolved.
    const afterPaint = (callback: () => void): void => {
        afterOnePaint(() => afterOnePaint(callback));
    };

    const settle = ({ key, outcome, reason, context }: SettleInteractionParams<Context>): void => {
        const interaction = open.get(key);

        if (!interaction) {
            return;
        }

        // Cleared before emitting, so an interaction can only ever report once.
        open.delete(key);
        clearTimeout(interaction.timeoutId);

        // Read before the paint callback, so the two frames of scheduling stay out of the number.
        const elapsed = globalThis.performance.now() - interaction.startedAt;
        const attributes: CategoricalAttributes = { ...interaction.context, ...context };

        afterPaint(() => {
            countMetric(config.outcomeMetric, 1, {
                ...attributes,
                outcome: outcome,
                reason: outcome === 'failure' ? reason : undefined,
            });

            if (outcome !== 'abort') {
                distributionMetric(config.durationMetric, elapsed, { ...attributes, outcome: outcome });
            }
        });
    };

    /** Outcome only, never a duration: nobody waited for a result they walked away from. */
    const abort = (key: string): void => settle({ key: key, outcome: 'abort' });

    /** A second start on the same key aborts the one it supersedes. */
    const start = ({ key, context }: StartInteractionParams<Context>): void => {
        abort(key);

        const timeoutId = setTimeout(() => {
            settle({ key: key, outcome: 'failure', reason: config.timeoutReason });
        }, config.timeoutMs);

        // Copied, so a caller that reuses its context object cannot rewrite an interaction already in flight.
        open.set(key, { context: { ...context }, startedAt: globalThis.performance.now(), timeoutId: timeoutId });
    };

    const isOpen = (key: string): boolean => open.has(key);

    return { start: start, settle: settle, abort: abort, isOpen: isOpen };
};

export { createInteractionTracker, type InteractionTracker };
